/**
 * Draft TTB DSP monthly reports for the Spirits module:
 *   TTB F 5110.40 Production, TTB F 5110.11 Storage, TTB F 5110.28 Processing.
 *
 * Tilly fills the lines it can compute from the spirits ledger. Everything
 * else stays blank for hand entry, and any line can be overridden here before
 * printing. Every sheet prints DRAFT and unsigned — nothing is filed from Tilly.
 * Spirits only: no beer, brewery TTB or QuickBooks data.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import '../App.css';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';
import { centralToday } from '../utils/centralDate';

const API = `${API_BASE_URL}/api/liquor/ttb-reports`;

const SITES = [
  { siteId: 'DSP-AL-20010', name: 'Madison Distillery' },
  { siteId: 'DSP-AL-20051', name: 'Athens Distillery' },
];

const FORM_ORDER = ['5110.40', '5110.11', '5110.28'] as const;
const FORM_TAB: Record<string, string> = {
  '5110.40': 'Production · 5110.40',
  '5110.11': 'Storage · 5110.11',
  '5110.28': 'Processing · 5110.28',
};

type Column = { key: string; label: string; group?: string; text?: boolean };
type Line = { no: string; label: string; kind: 'input' | 'derived'; section?: string };
type Part = { key: string; title: string; whole?: boolean; columns: Column[]; lines: Line[] };
type Layout = { formCode: string; title: string; edition: string; omb?: string; reportCovers?: string; parts: Part[] };
type Cell = { value: number | null; text?: string; source: 'tilly' | 'filed' | 'book' | 'override' | 'derived'; computed?: number | null };
type FormData = { layout: Layout; cells: Record<string, Cell>; overrideCount: number };
type ChecklistItem = { level: 'error' | 'warn' | 'info'; code: string; message: string; form?: string };
type Report = {
  draft: true;
  siteId: string;
  ym: string;
  period: { start: string; end: string; label: string; priorYm: string; priorLabel: string };
  taxDetermination: 'bottling' | 'removal';
  header: { proprietor: string; ein: string; plantNumber: string; siteName: string; location: string; monthYear: string };
  forms: Record<string, FormData>;
  checklist: ChecklistItem[];
  priorClosings?: { formCode: string; cellKey: string; value: number | null }[];
};

const CLOSING_LINES: Record<string, { closing: string; opening: string; columns: string[] }[]> = {
  '5110.11': [{ closing: '23', opening: '1', columns: ['b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l'] }],
  '5110.28': [
    { closing: '25', opening: '1', columns: ['b', 'c'] },
    { closing: '46', opening: '27', columns: ['b', 'c'] },
  ],
};

async function call<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(url, init);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((body && body.error) || `HTTP ${res.status}`);
  return body as T;
}

const put = (url: string, body: unknown) =>
  call<{ ok: boolean }>(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

export function formatCell(value: number | null | undefined, whole?: boolean): string {
  if (value == null || !Number.isFinite(Number(value))) return '';
  const n = Number(value);
  return whole ? String(Math.round(n)) : n.toFixed(2);
}

function printSheets(all: boolean) {
  document.body.classList.add('dsp5110-printing');
  if (all) document.body.classList.add('dsp5110-print-all');
  const cleanup = () => {
    document.body.classList.remove('dsp5110-printing', 'dsp5110-print-all');
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);
  window.print();
}

const Styles: React.FC = () => (
  <style>{`
    .dsp5110-sheet { position: relative; background: #fff; color: #000; border: 1px solid #999; padding: 14px 16px; margin-bottom: 18px; overflow-x: auto; }
    .dsp5110-watermark { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: none;
      font-size: 120px; font-weight: 800; letter-spacing: 12px; color: rgba(200, 0, 0, 0.12); transform: rotate(-18deg); z-index: 0; }
    .dsp5110-draft-tag { color: #b00; font-weight: 700; border: 2px solid #b00; padding: 1px 8px; display: inline-block; }
    .dsp5110-head { display: grid; grid-template-columns: 2fr 1fr; gap: 6px 18px; font-size: 12px; margin-bottom: 8px; position: relative; z-index: 1; }
    .dsp5110-head .box { border: 1px solid #000; padding: 3px 6px; }
    .dsp5110-head .lbl { font-size: 9px; text-transform: uppercase; display: block; }
    .dsp5110-title { display: flex; justify-content: space-between; align-items: baseline; font-size: 13px; margin-bottom: 6px; position: relative; z-index: 1; }
    .dsp5110-title h3 { font-size: 15px; margin: 0; }
    .dsp5110-part { font-weight: 700; font-size: 12px; background: #e8e8e8; border: 1px solid #000; border-bottom: 0; padding: 2px 6px; margin-top: 8px; position: relative; z-index: 1; }
    .dsp5110-table { width: 100%; border-collapse: collapse; font-size: 11px; position: relative; z-index: 1; table-layout: fixed; }
    .dsp5110-table th, .dsp5110-table td { border: 1px solid #000; padding: 1px 4px; vertical-align: middle; }
    .dsp5110-table th { font-weight: 600; font-size: 9.5px; text-align: center; }
    .dsp5110-table td.no { width: 34px; text-align: center; font-weight: 600; }
    .dsp5110-table td.lbl { width: 210px; }
    .dsp5110-table td.num { text-align: right; font-variant-numeric: tabular-nums; height: 18px; }
    .dsp5110-table tr.derived td { font-weight: 700; }
    .dsp5110-table td.src-override { background: #fff3b0; }
    .dsp5110-table td.src-book { background: #fde2e2; }
    .dsp5110-table input { width: 100%; font-size: 11px; text-align: right; border: 1px solid #88a; padding: 0 2px; }
    .dsp5110-sign { display: grid; grid-template-columns: 3fr 2fr 1fr; gap: 10px; font-size: 11px; margin-top: 10px; position: relative; z-index: 1; }
    .dsp5110-sign .line { border-bottom: 1px solid #000; min-height: 26px; }
    .dsp5110-perjury { font-size: 10px; margin-top: 10px; position: relative; z-index: 1; }
    .dsp5110-check { border-left: 4px solid #c80; background: #fff8e6; padding: 8px 12px; margin-bottom: 12px; }
    .dsp5110-check.has-error { border-left-color: #b00; background: #fdecec; }
    .dsp5110-check li { margin: 2px 0; }
    .dsp5110-tabs { display: flex; gap: 6px; flex-wrap: wrap; margin: 8px 0 12px; }
    .dsp5110-tabs button[aria-pressed='true'] { font-weight: 700; outline: 2px solid #335; }
    .dsp5110-legend span { display: inline-block; padding: 0 6px; margin-right: 8px; border: 1px solid #aaa; font-size: 12px; }
    @media print {
      @page dsp5110 { size: 11in 8.5in; margin: 0.35in; }
      body.dsp5110-printing .no-print,
      body.dsp5110-printing .menu,
      body.dsp5110-printing .navbar-mobile { display: none !important; }
      body.dsp5110-printing .dsp5110-sheet { page: dsp5110; border: 0; padding: 0; margin: 0; overflow: visible; break-after: page; }
      body.dsp5110-printing .dsp5110-sheet.inactive { display: none; }
      body.dsp5110-printing.dsp5110-print-all .dsp5110-sheet.inactive { display: block; }
      body.dsp5110-printing .dsp5110-table td.src-override,
      body.dsp5110-printing .dsp5110-table td.src-book { background: none; }
      body.dsp5110-printing .dsp5110-watermark { color: rgba(200, 0, 0, 0.18); -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  `}</style>
);

const PERJURY: Record<string, string> = {
  '5110.40':
    'Under penalties of perjury, I declare that I have examined this report, and to the best of my knowledge and belief, it is true, correct, and complete and is supported by true, correct, and complete commercial records which are available for inspection.',
  '5110.11':
    'Under penalties of perjury, I declare that I have examined this report and, to the best of my knowledge and belief, it is a true, correct, and complete report of storage operations, and is supported by true, correct, and complete commercial records which are available for inspection.',
  '5110.28':
    'UNDER PENALTIES OF PERJURY, I DECLARE that I have examined this report and, to the best of my knowledge and belief, it is a true and complete report.',
  default: '',
};

type EditProps = {
  editing: boolean;
  onSave: (formCode: string, cellKey: string, raw: string) => void;
  onClear: (formCode: string, cellKey: string) => void;
};

const NumCell: React.FC<{ formCode: string; cellKey: string; cell?: Cell; whole?: boolean; derived: boolean } & EditProps> = ({
  formCode,
  cellKey,
  cell,
  whole,
  derived,
  editing,
  onSave,
  onClear,
}) => {
  const shown = formatCell(cell?.value, whole);
  const [draft, setDraft] = useState(shown);
  useEffect(() => setDraft(shown), [shown]);
  const cls = `num${cell ? ` src-${cell.source}` : ''}`;
  const title = cell
    ? cell.source === 'override'
      ? `Typed in${cell.computed != null ? ` (Tilly: ${formatCell(cell.computed, whole)})` : ''}`
      : cell.source === 'book'
      ? "Tilly book balance — no filed closing keyed in"
      : cell.source === 'filed'
      ? 'From last month\u2019s filed closing'
      : cell.source === 'derived'
      ? 'Total'
      : 'From Tilly'
    : undefined;
  if (!editing || derived) {
    return (
      <td className={cls} title={title} data-testid={`cell-${formCode}-${cellKey}`}>
        {shown}
      </td>
    );
  }
  return (
    <td className={cls} title={title} data-testid={`cell-${formCode}-${cellKey}`}>
      <div style={{ display: 'flex', gap: 2 }}>
        <input
          aria-label={`${formCode} line ${cellKey}`}
          inputMode="decimal"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            if (draft.trim() !== shown) onSave(formCode, cellKey, draft.trim());
          }}
        />
        {cell?.source === 'override' && (
          <button type="button" className="no-print" title="Back to Tilly's figure" onClick={() => onClear(formCode, cellKey)} style={{ padding: '0 3px', fontSize: 10 }}>
            ↺
          </button>
        )}
      </div>
    </td>
  );
};

const PartTable: React.FC<{ formCode: string; part: Part; cells: Record<string, Cell> } & EditProps> = ({ formCode, part, cells, ...edit }) => {
  const groups = useMemo(() => {
    const out: { label: string; span: number }[] = [];
    part.columns.forEach((c) => {
      const g = c.group || '';
      const last = out[out.length - 1];
      if (last && g && last.label === g) last.span += 1;
      else out.push({ label: g, span: 1 });
    });
    return out;
  }, [part.columns]);
  const hasGroups = part.columns.some((c) => c.group);
  return (
    <>
      <div className="dsp5110-part">{part.title}</div>
      <table className="dsp5110-table">
        <thead>
          {hasGroups && (
            <tr>
              <th colSpan={2} rowSpan={2}>
                Line / description
                <br />
                (a)
              </th>
              {groups.map((g, i) => (
                <th key={i} colSpan={g.span} rowSpan={g.label ? 1 : 2}>
                  {g.label || part.columns[groups.slice(0, i).reduce((n, x) => n + x.span, 0)].label}
                  {!g.label && <div>({part.columns[groups.slice(0, i).reduce((n, x) => n + x.span, 0)].key})</div>}
                </th>
              ))}
            </tr>
          )}
          <tr>
            {!hasGroups && (
              <th colSpan={2}>
                Line / description
              </th>
            )}
            {part.columns
              .filter((c) => !hasGroups || c.group)
              .map((c) => (
                <th key={c.key}>
                  {c.label}
                  <div>({c.key})</div>
                </th>
              ))}
          </tr>
        </thead>
        <tbody>
          {part.lines.map((l) => (
            <React.Fragment key={l.no}>
            {l.section && (
              <tr>
                <td colSpan={part.columns.length + 2} style={{ fontWeight: 700, textAlign: 'center' }}>
                  {l.section.toUpperCase()}
                </td>
              </tr>
            )}
            <tr className={l.kind === 'derived' ? 'derived' : undefined}>
              <td className="no">{l.no.replace(/^(II|III|IV|V|VI)(?=\d)/, '')}</td>
              <td className="lbl">{l.label}</td>
              {part.columns.map((c) =>
                c.text ? (
                  <td key={c.key} data-testid={`cell-${formCode}-${l.no}.${c.key}`}>
                    {cells[`${l.no}.${c.key}`]?.text || ''}
                  </td>
                ) : (
                  <NumCell
                    key={c.key}
                    formCode={formCode}
                    cellKey={`${l.no}.${c.key}`}
                    cell={cells[`${l.no}.${c.key}`]}
                    whole={part.whole}
                    derived={l.kind === 'derived'}
                    {...edit}
                  />
                )
              )}
            </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </>
  );
};

const Sheet: React.FC<{ formCode: string; report: Report; active: boolean } & EditProps> = ({ formCode, report, active, ...edit }) => {
  const form = report.forms[formCode];
  if (!form) return null;
  const { layout, cells } = form;
  const h = report.header;
  return (
    <article className={`dsp5110-sheet${active ? '' : ' inactive'}`} data-testid={`sheet-${formCode}`} style={active ? undefined : { display: 'none' }}>
      <div className="dsp5110-watermark" aria-hidden="true">
        DRAFT
      </div>
      <div className="dsp5110-title">
        <h3>
          {layout.title}
          {layout.reportCovers ? ` — ${layout.reportCovers}` : ''}
        </h3>
        <span>
          <span className="dsp5110-draft-tag">DRAFT — NOT FILED</span>
          {layout.omb ? ` · OMB No. ${layout.omb}` : ''}
        </span>
      </div>
      <div className="dsp5110-head">
        <div className="box">
          <span className="lbl">1. Name and address of proprietor</span>
          {h.proprietor}
          <br />
          {h.location}
        </div>
        <div className="box">
          <span className="lbl">2. Plant registry number</span>
          {h.plantNumber}
          <span className="lbl" style={{ marginTop: 4 }}>
            3. Employer identification number
          </span>
          {h.ein}
        </div>
        <div className="box">
          <span className="lbl">Distillery</span>
          {h.siteName}
        </div>
        <div className="box">
          <span className="lbl">4. Report for month of</span>
          {h.monthYear}
        </div>
      </div>
      {layout.parts.map((p) => (
        <PartTable key={p.key} formCode={formCode} part={p} cells={cells} {...edit} />
      ))}
      <p className="dsp5110-perjury">{PERJURY[formCode] || PERJURY.default}</p>
      <div className="dsp5110-sign">
        <div>
          Signature of proprietor or authorized person
          <div className="line" />
        </div>
        <div>
          Title
          <div className="line" />
        </div>
        <div>
          Date
          <div className="line" />
        </div>
      </div>
      <p className="dsp5110-perjury" style={{ fontStyle: 'italic' }}>
        Draft prepared in Tilly from the spirits ledger. Unsigned. Check every line against the records before filing.
      </p>
    </article>
  );
};

const PriorClosingForm: React.FC<{ report: Report; onSaved: () => void }> = ({ report, onSaved }) => {
  const [values, setValues] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    const v: Record<string, string> = {};
    (report.priorClosings || []).forEach((p) => {
      v[`${p.formCode}|${p.cellKey}`] = p.value == null ? '' : String(p.value);
    });
    setValues(v);
    setMsg(null);
  }, [report]);
  const save = async (formCode: string) => {
    const out: Record<string, string> = {};
    Object.entries(values).forEach(([k, v]) => {
      const [f, key] = k.split('|');
      if (f === formCode && v.trim() !== '') out[key] = v.trim();
    });
    try {
      await put(`${API}/prior-closing`, { siteId: report.siteId, formCode, periodYm: report.period.priorYm, values: out });
      setMsg(`Saved ${report.period.priorLabel} closing for ${formCode}.`);
      onSaved();
    } catch (err: unknown) {
      setMsg(err instanceof Error ? err.message : 'Save failed');
    }
  };
  return (
    <details className="no-print" style={{ marginBottom: 12 }} data-testid="prior-closing">
      <summary>
        <strong>Opening balances:</strong> key in the {report.period.priorLabel} closing from the filed reports
      </summary>
      <p className="flow-hint">
        Type the end-of-month figures exactly as filed. They become this month&apos;s line 1 (and line 27 on 5110.28). Leave a box blank
        if the filed report left it blank.
      </p>
      {Object.entries(CLOSING_LINES).map(([formCode, rows]) => {
        const layout = report.forms[formCode]?.layout;
        if (!layout) return null;
        return (
          <div key={formCode} style={{ marginBottom: 10 }}>
            <h4 className="h6">{FORM_TAB[formCode]}</h4>
            {rows.map((r) => {
              const part = layout.parts.find((p) => p.lines.some((l) => l.no === r.closing));
              const line = part?.lines.find((l) => l.no === r.closing);
              return (
                <div key={r.closing} style={{ overflowX: 'auto' }}>
                  <div style={{ fontSize: 13 }}>
                    Line {r.closing} · {line?.label}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {r.columns.map((c) => {
                      const col = part?.columns.find((x) => x.key === c);
                      const k = `${formCode}|${r.closing}.${c}`;
                      return (
                        <label key={c} style={{ fontSize: 11, width: 92 }}>
                          ({c}) {col?.label}
                          <input
                            inputMode="decimal"
                            value={values[k] ?? ''}
                            onChange={(e) => setValues({ ...values, [k]: e.target.value })}
                            data-testid={`prior-${formCode}-${r.closing}.${c}`}
                            style={{ width: '100%' }}
                          />
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <button type="button" onClick={() => save(formCode)} style={{ marginTop: 4 }}>
              Save {formCode} opening
            </button>
          </div>
        );
      })}
      {msg && <p className="flow-hint">{msg}</p>}
    </details>
  );
};

export const SpiritsTtbReports: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const siteId = params.get('siteId') || SITES[0].siteId;
  const month = params.get('month') || centralToday().slice(0, 7);
  const tab = (params.get('form') as string) || '5110.11';
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    next.set(k, v);
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    try {
      setReport(await call<Report>(`${API}?siteId=${encodeURIComponent(siteId)}&month=${encodeURIComponent(month)}`));
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load reports');
    }
  }, [siteId, month]);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = async (formCode: string, cellKey: string, raw: string) => {
    try {
      await put(`${API}/override`, { siteId, formCode, periodYm: month, cellKey, value: raw === '' ? null : raw });
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  };
  const onClear = async (formCode: string, cellKey: string) => {
    try {
      await put(`${API}/override`, { siteId, formCode, periodYm: month, cellKey, clear: true });
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  };
  const setTax = async (mode: 'bottling' | 'removal') => {
    try {
      await put(`${API}/settings`, { siteId, taxDetermination: mode });
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  };

  const hasError = report?.checklist.some((c) => c.level === 'error');

  return (
    <div className="page-container container" data-testid="spirits-ttb-reports">
      <Styles />
      <div className="flow-toolbar no-print" style={{ marginBottom: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Link to="/spirits">← Spirits</Link>
        <button type="button" onClick={() => printSheets(false)} disabled={!report}>
          Print this form
        </button>
        <button type="button" onClick={() => printSheets(true)} disabled={!report}>
          Print all three
        </button>
      </div>
      <h2 className="app-header no-print">TTB monthly reports (draft)</h2>
      <p className="flow-hint no-print">
        Drafts of the DSP monthly reports. Tilly fills what it can from the spirits ledger; blank lines are for you. Nothing here is
        filed — every sheet prints DRAFT and unsigned.
      </p>
      <div className="flow-field-grid no-print" style={{ marginBottom: 12 }}>
        <div className="flow-field">
          <label htmlFor="ttb5110-site">Distillery</label>
          <select id="ttb5110-site" value={siteId} onChange={(e) => setParam('siteId', e.target.value)} data-testid="ttb5110-site">
            {SITES.map((s) => (
              <option key={s.siteId} value={s.siteId}>
                {s.name} ({s.siteId})
              </option>
            ))}
          </select>
        </div>
        <div className="flow-field">
          <label htmlFor="ttb5110-month">Month</label>
          <input id="ttb5110-month" type="month" value={month} onChange={(e) => e.target.value && setParam('month', e.target.value)} />
        </div>
        <fieldset className="flow-field">
          <legend style={{ fontSize: 14 }}>Tax determination (5110.28 Part II)</legend>
          <label style={{ marginRight: 10 }}>
            <input
              type="radio"
              name="ttb5110-tax"
              checked={report?.taxDetermination !== 'removal'}
              onChange={() => setTax('bottling')}
              data-testid="tax-bottling"
            />{' '}
            At bottling
          </label>
          <label>
            <input
              type="radio"
              name="ttb5110-tax"
              checked={report?.taxDetermination === 'removal'}
              onChange={() => setTax('removal')}
              data-testid="tax-removal"
            />{' '}
            At removal
          </label>
        </fieldset>
      </div>
      {error && (
        <p className="error no-print" role="alert">
          {error}
        </p>
      )}
      {report && (
        <>
          {report.checklist.length > 0 && (
            <div className={`dsp5110-check no-print${hasError ? ' has-error' : ''}`} role="status" data-testid="ttb5110-checklist">
              <strong>Before you print</strong>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {report.checklist.map((c, i) => (
                  <li key={i}>
                    <strong>{c.level === 'error' ? 'Fix' : c.level === 'warn' ? 'Check' : 'Note'}:</strong> {c.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <PriorClosingForm report={report} onSaved={load} />
          <div className="dsp5110-tabs no-print" role="group" aria-label="Form">
            {FORM_ORDER.map((f) => (
              <button key={f} type="button" aria-pressed={tab === f} onClick={() => setParam('form', f)} data-testid={`tab-${f}`}>
                {FORM_TAB[f]}
                {report.forms[f]?.overrideCount ? ` (${report.forms[f].overrideCount} typed)` : ''}
              </button>
            ))}
            <label style={{ marginLeft: 'auto' }}>
              <input type="checkbox" checked={editing} onChange={(e) => setEditing(e.target.checked)} data-testid="ttb5110-edit" /> Type
              over lines
            </label>
          </div>
          <p className="dsp5110-legend no-print">
            <span style={{ background: '#fff3b0' }}>typed in</span>
            <span style={{ background: '#fde2e2' }}>Tilly book opening (no filed closing)</span>
            {editing && <span>Clear a box to force it blank; ↺ puts Tilly&apos;s figure back.</span>}
          </p>
          {FORM_ORDER.map((f) => (
            <Sheet key={f} formCode={f} report={report} active={tab === f} editing={editing} onSave={onSave} onClear={onClear} />
          ))}
        </>
      )}
    </div>
  );
};

export default SpiritsTtbReports;
